import { AdditiveBlending, Color, MeshStandardMaterial, ShaderMaterial, use } from '@anov/3d-core'
import type { Material, Mesh, Object3D, Vector3 } from '@anov/3d-core'
import { frag, frag_pre, ver, ver_pre } from './shader/realFade'
import { frag as fragFinal, ver as verFinal } from './shader/finalFade'
import { ground } from './shader/ground'
import { SwitchType } from './MaterialSwitchSystem'

const finirColor = new Color('#2A78D9')
const lowFinirColor = new Color('#15407A') // 矮楼finir

const heightTransitionEffect = /* glsl */`
        float normalDotY = abs(dot(normal, vec3(0.0, 1.0, 0.0)));

        if (normalDotY < 0.8) {
            float interval = u_interval;
            float slice_height = u_slice_height;
            float y_pos = vWorldPosition.y + u_height;
            float section = floor(y_pos / interval);
            float section_start = section * interval;

            if (y_pos >= section_start && y_pos < section_start + slice_height) {
                float heightDelta = (y_pos - section_start) / slice_height; // 归一化高度 0-1
                vec4 sliceColor = vec4(0.0, 0.2, 0.5, 1.0);
                float mixFactor = smoothstep(0.0, 1.0, heightDelta);
                gl_FragColor = mix(sliceColor, gl_FragColor, mixFactor);
            }
        }
        `

const injectBaseFinir = (shader, power = 0.7, color = finirColor, fragmentShader = '') => {
  shader.uniforms.rimColor = { value: color }
  shader.uniforms.rimPower = { value: power }

  shader.fragmentShader = /* glsl */`
            uniform vec3 rimColor;
            uniform float rimPower;
            uniform float u_height;
            uniform float u_interval;
            uniform float u_slice_height;
            varying vec3 vViewDirection;
            ${shader.fragmentShader.replace(
    '#include <dithering_fragment>',
    `
    #include <dithering_fragment>
    vec3 viewDir = normalize(vViewDirection);
    float dotNV = 1.0-saturate( dot( normal, viewDir ) );
    gl_FragColor.rgb += rimPower*dotNV*rimColor; 
    ${fragmentShader}`,
  )}
          `
}

type BaseOption = {
  transparent: boolean
  depthTest: boolean
  depthWrite: boolean
  polygonOffset: boolean
  polygonOffsetFactor: number
  polygonOffsetUnits: number
  alphaTest: number
  blending?: number
}

type ShaderOption = {
  uniforms: Record<string, { value: any }>
  vertexShader: string
  fragmentShader: string
  preVertexShader: string
  preFragmentShader: string
  callback?: (shader: ShaderMaterial) => void
}

const modifyTheMaterial = (material, baseOption: BaseOption, shaderOption: ShaderOption) => {
  const { transparent, depthTest, depthWrite, polygonOffset, polygonOffsetFactor, polygonOffsetUnits, alphaTest = 0.05, blending } = baseOption
  const { uniforms, vertexShader, fragmentShader, preVertexShader, preFragmentShader, callback } = shaderOption

  material.transparent = transparent
  material.depthTest = depthTest
  material.depthWrite = depthWrite
  material.polygonOffset = polygonOffset
  material.polygonOffsetFactor = polygonOffsetFactor
  material.polygonOffsetUnits = polygonOffsetUnits
  material.alphaTest = alphaTest
  blending && (material.blending = blending)

  material.onBeforeCompile = (shader) => {
    shader.uniforms = {
      ...shader.uniforms,
      ...uniforms,
    }

    shader.vertexShader = preVertexShader + shader.vertexShader
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>', vertexShader,
    )
    shader.fragmentShader = preFragmentShader + shader.fragmentShader
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>', fragmentShader,
    )

    callback && callback(shader)
  }

  material.needsUpdate = true
}

const conversionRealFade = (material: Material, type: 'Fresnel' | 'Frame' | 'Pure', uniforms: { maxSize: { value: number }; center: { value: Vector3 }; progress: { value: number }; isRealFadeIn: { value: boolean } }) => {
  const newMaterial = material.clone()

  modifyTheMaterial(newMaterial,
    {
      transparent: true,
      depthTest: true,
      depthWrite: true,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
      alphaTest: 0.05,
    },
    {
      uniforms: {
        progress: uniforms.progress,
        objSize: uniforms.maxSize,
        center: uniforms.center,
        isFadeIn: uniforms[`is${type}FadeIn`],
      },
      vertexShader: ver,
      fragmentShader: frag,
      preVertexShader: ver_pre,
      preFragmentShader: frag_pre,
    })

  return newMaterial
}

/**
 * inject to final fade
 * @param object
 * @param maxSize
 * @param uniforms
 */
export const injectToFrameFade = (object: Object3D, uniforms: { maxSize: { value: number }; center: { value: Vector3 }; progress: { value: number }; isFrameFadeIn: { value: boolean } }) => {
  object.traverse((child: any) => {
    if (child.isMesh) {
      child.material = child.material.clone();
      (child as Mesh).userData.originalMaterial = conversionRealFade(child.material, SwitchType.Frame, uniforms as any)
      child.material.side = 2
      child.material.transparent = true
      child.material.depthTest = true
      child.material.depthWrite = true
      child.material.blending = AdditiveBlending
      child.material.polygonOffset = true
      child.material.polygonOffsetFactor = 0
      child.material.polygonOffsetUnits = 0
      child.material.alphaTest = 0.35

      child.material.onBeforeCompile = (shader) => {
        shader.uniforms = {
          ...shader.uniforms,
          progress: uniforms.progress,
          center: uniforms.center,
          addOpacity: { value: 0.7 },
          objSize: uniforms.maxSize,
          isFadeIn: uniforms.isFrameFadeIn,
        }
        shader.vertexShader = verFinal
        shader.fragmentShader = fragFinal
      }

      child.material.needsUpdate = true
    }
  })
}

/**
 * inject to real fade
 * @param object
 * @param uniforms
 */
export const injectToRealFade = (object: Object3D, uniforms: { maxSize: { value: number }; center: { value: Vector3 }; progress: { value: number }; isRealFadeIn: { value: boolean } }) => {
  object.traverse((child: any) => {
    if (child.isMesh) {
      (child as Mesh).userData.originalMaterial = child.material
      const newMaterial = child.material.clone()

      modifyTheMaterial(newMaterial,
        {
          transparent: true,
          depthTest: true,
          depthWrite: true,
          polygonOffset: true,
          polygonOffsetFactor: 1,
          polygonOffsetUnits: 1,
          alphaTest: 0.05,
        },
        {
          uniforms: {
            progress: uniforms.progress,
            objSize: uniforms.maxSize,
            isFadeIn: uniforms.isRealFadeIn,
            center: uniforms.center,
          },
          vertexShader: ver,
          fragmentShader: frag,
          preVertexShader: ver_pre,
          preFragmentShader: frag_pre,
        })

      child.material = newMaterial
    }
  })
}

/**
 * inject to base write
 * @param object
 * @param uniforms
 */
export const injectToBaseWrite = (object: Object3D, uniforms: { maxSize: { value: number }; center: { value: Vector3 }; progress: { value: number }; isPureFadeIn: { value: boolean } }) => {
  object.traverse((child: any) => {
    if (child.isMesh) {
      let newMaterial = child.material.clone();
      (child as Mesh).userData.originalMaterial = conversionRealFade(child.material, SwitchType.Pure, uniforms as any)
      newMaterial = new MeshStandardMaterial({ color: new Color('#fff') })

      modifyTheMaterial(newMaterial,
        {
          transparent: true,
          depthTest: true,
          depthWrite: true,
          polygonOffset: true,
          polygonOffsetFactor: 2,
          polygonOffsetUnits: 2,
          alphaTest: 0.05,
        },
        {
          uniforms: {
            progress: uniforms.progress,
            objSize: uniforms.maxSize,
            isFadeIn: uniforms.isPureFadeIn,
            center: uniforms.center,
          },
          vertexShader: ver,
          fragmentShader: frag,
          preVertexShader: ver_pre,
          preFragmentShader: frag_pre,
          callback: (shader) => {
            injectBaseFinir(shader, 0.3, new Color('#fff'))
          },
        })

      child.material = newMaterial
    }
  })
}

/**
 * inject to finir material
 * @param object
 * @param uniforms
 */
export const injectToFinirMaterial = (object: Object3D, uniforms: { maxSize: { value: number }; center: { value: Vector3 }; progress: { value: number }; isFresnelFadeIn: { value: boolean } }) => {
  object.traverse((child: any) => {
    if (child.isMesh) {
      let newMaterial = child.material.clone();
      (child as Mesh).userData.originalMaterial = conversionRealFade(child.material, SwitchType.Fresnel, uniforms as any)
      newMaterial = new MeshStandardMaterial({ color: new Color('#000') })

      modifyTheMaterial(newMaterial,
        {
          transparent: true,
          depthTest: true,
          depthWrite: true,
          polygonOffset: true,
          polygonOffsetFactor: 3,
          polygonOffsetUnits: 3,
          alphaTest: 0.05,
        },
        {
          uniforms: {
            progress: uniforms.progress,
            objSize: uniforms.maxSize,
            isFadeIn: uniforms.isFresnelFadeIn,
            center: uniforms.center,
          },
          vertexShader: ver,
          fragmentShader: frag,
          preVertexShader: ver_pre,
          preFragmentShader: frag_pre,
          callback: (shader) => {
            injectBaseFinir(shader)
          },
        })

      child.material = newMaterial
    }
  })
}

export const injectToGround = (
  object: Mesh,
  type: 'Real' | 'Fresnel' | 'Frame' | 'Pure',
  uniforms: { maxSize: { value: number }; center: { value: Vector3 }; progress: { value: number }; time: { value: number }; isApi: { value: boolean } },
) => {
  object.material = new ShaderMaterial({
    uniforms: {
      ...ground.uniforms,
      progress: uniforms.progress,
      objSize: uniforms.maxSize,
      isFadeIn: uniforms[`is${type}FadeIn`],
      center: uniforms.center,
      time: uniforms.time,
      isApi: uniforms.isApi,
    },
    vertexShader: ground.vertexShader,
    fragmentShader: ground.fragmentShader,
    transparent: true,
    side: 2,
    depthTest: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: 5,
    polygonOffsetUnits: 5,
    alphaTest: 0.05,
  })

  object.material.needsUpdate = true
}

export const inject = (
  material: Material,
  type: 'Fresnel' | 'Frame' | 'Pure',
  uniforms: { maxSize: { value: number }; center: { value: Vector3 }; progress: { value: number }; height: { value: number } },
  isSweepAway = false,
) => {
  material.transparent = true
  material.depthTest = true
  material.depthWrite = true
  material.alphaTest = 0.05

  material.onBeforeCompile = (shader) => {
    shader.uniforms = {
      ...shader.uniforms,
      progress: uniforms.progress,
      objSize: uniforms.maxSize,
      isFadeIn: uniforms[`is${type}FadeIn`],
      center: uniforms.center,
      u_interval: { value: 100.0 },
      u_slice_height: { value: 80.0 },
    }

    shader.vertexShader = ver_pre + shader.vertexShader
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>', ver,
    )
    shader.fragmentShader = frag_pre + shader.fragmentShader
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>', frag,
    )

    shader.uniforms.u_height = uniforms.height

    shader.fragmentShader = /* glsl */`
            uniform float u_height;
            uniform float u_interval;
            uniform float u_slice_height;
            
            ${shader.fragmentShader.replace(
      '#include <dithering_fragment>',
      '#include <dithering_fragment>\n\t'
      + `${isSweepAway ? heightTransitionEffect : ''}`,
    )}
          `
  }

  material.needsUpdate = true

  return material
}

export const finirheightTransitionEffect = (material: Material, uniforms: { height: { value: number }; maxSize: { value: number }; center: { value: Vector3 }; progress: { value: number }; isFresnelFadeIn: { value: boolean } }) => {
  const newMaterial = material.clone()
  newMaterial.transparent = true
  newMaterial.depthTest = true
  newMaterial.depthWrite = true
  newMaterial.polygonOffset = true
  newMaterial.polygonOffsetFactor = 3
  newMaterial.polygonOffsetUnits = 3
  newMaterial.alphaTest = 0.05

  newMaterial.onBeforeCompile = (shader) => {
    shader.uniforms = {
      ...shader.uniforms,
      progress: uniforms.progress,
      objSize: uniforms.maxSize,
      isFadeIn: uniforms.isFresnelFadeIn,
      center: uniforms.center,
      u_interval: { value: 100.0 },
      u_slice_height: { value: 80.0 },
      u_time: { value: 0 },
      u_speed: { value: 0.5 },
    }

    shader.vertexShader = ver_pre + shader.vertexShader
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>', ver,
    )
    shader.fragmentShader = frag_pre + shader.fragmentShader
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>', frag,
    )

    shader.uniforms.u_height = uniforms.height

    injectBaseFinir(shader, 0.7, finirColor, heightTransitionEffect)
  }

  newMaterial.needsUpdate = true

  return newMaterial
}

export const finirlowEffect = (material: Material, uniforms: { height: { value: number }; maxSize: { value: number }; center: { value: Vector3 }; progress: { value: number }; isFresnelFadeIn: { value: boolean } }) => {
  const newMaterial = material.clone()

  modifyTheMaterial(newMaterial,
    {
      transparent: true,
      depthTest: true,
      depthWrite: true,
      polygonOffset: true,
      polygonOffsetFactor: 3,
      polygonOffsetUnits: 3,
      alphaTest: 0.05,
    },
    {
      uniforms: {
        progress: uniforms.progress,
        objSize: uniforms.maxSize,
        isFadeIn: uniforms.isFresnelFadeIn,
        center: uniforms.center,
        u_interval: { value: 100.0 },
        u_slice_height: { value: 80.0 },
        u_time: { value: 0 },
        u_speed: { value: 0.5 },
      },
      vertexShader: ver,
      fragmentShader: frag,
      preVertexShader: ver_pre,
      preFragmentShader: frag_pre,
      callback: (shader) => {
        injectBaseFinir(shader, 0.7, lowFinirColor)
      },
    })

  return newMaterial
}
